import React from 'react';
import { Typography, useTheme, LinearProgress } from '@material-ui/core';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';

import { agingOptions } from './agingOptions';
import { countOptions } from './countOptions';
import { useStyles } from './styles';
import 'react-datepicker/dist/react-datepicker.css';

interface ISerie {
  name: string;
  data: number[];
}

interface StackedBarProps {
  title?: string;
  showTitle?: boolean;
  labels: any;
  data: ISerie[];
  type: 'count' | 'aging';
  marginBottom?: number;
  showDatePicker?: boolean;
  xAxisTitle?: string;
  yAxisTitle?: string;
  colors?: string[];
  loading?: boolean;
  showLegend?: boolean;
  legendPosition?: 'bottom' | 'left' | 'right' | 'top';
  legendFontSize?: string;
  height?: number | string;
  dataType?: 'category' | 'datetime' | 'numeric' | undefined;
  showXLabels?: boolean;
}

const CountOrdersChart: React.FC<StackedBarProps> = ({
  title,
  showTitle,
  labels,
  data,
  type,
  marginBottom,
  xAxisTitle,
  yAxisTitle,
  colors,
  loading,
  showLegend,
  legendPosition,
  legendFontSize,
  dataType,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <div className={classes.root}>
      {showTitle && title && (
        <Typography className={classes.title} variant="h3">
          {title}
        </Typography>
      )}
      {loading ? (
        <div className={classes.loadingContainer}>
          <div>
            <Typography className={classes.title} variant="subtitle2">
              {t('components:feedback.loading')}
            </Typography>
            <LinearProgress style={{ height: 16, borderRadius: 8 }} />
          </div>
        </div>
      ) : (
        <Chart
          options={
            type === 'count'
              ? {
                  ...countOptions,
                  xaxis: {
                    labels: {
                      show: true,
                      style: {
                        colors: '#000',
                        fontSize: '12px',
                      },
                    },
                    type: dataType || 'datetime',
                    categories: labels,
                    title: {
                      text: xAxisTitle || undefined,
                      style: {
                        fontWeight: 300,
                        fontFamily: 'Poppins',
                      },
                    },
                  },
                  yaxis: {
                    ...countOptions.yaxis,
                    title: {
                      text: yAxisTitle || undefined,
                      style: {
                        fontWeight: 300,
                        fontFamily: 'Poppins',
                      },
                    },
                  },
                  colors: colors || [
                    theme.charts.low,
                    theme.charts.average,
                    theme.charts.high,
                    theme.charts.urgent,
                  ],
                  legend: {
                    show: showLegend || false,
                    position: legendPosition || 'bottom',
                    fontSize: legendFontSize || '10%',
                    markers: {
                      radius: 25,
                    },
                  },
                }
              : {
                  ...agingOptions,
                  xaxis: {
                    categories: labels,
                    axisTicks: {
                      show: false,
                    },
                    labels: {
                      hideOverlappingLabels: false,
                      trim: true,
                      formatter(val) {
                        const nVal = Number(val);
                        return `${nVal.toFixed(0)}`;
                      },
                    },
                    title: {
                      text: xAxisTitle || undefined,
                      offsetY: -16,
                      style: {
                        fontWeight: 300,
                        fontFamily: 'Poppins',
                      },
                    },
                  },
                  yaxis: {
                    ...agingOptions.yaxis,
                    title: {
                      text: yAxisTitle || undefined,
                      style: {
                        fontWeight: 300,
                        fontFamily: 'Poppins',
                      },
                    },
                  },
                  colors: colors || [
                    theme.charts.low,
                    theme.charts.average,
                    theme.charts.high,
                    theme.charts.urgent,
                  ],
                  legend: {
                    show: showLegend || false,
                    position: legendPosition || 'bottom',
                    fontSize: legendFontSize || '10%',
                    markers: {
                      radius: 25,
                    },
                  },
                }
          }
          series={data}
          type="bar"
          width="100%"
          height="100%"
          // height={height || 240}
          style={{
            marginRight: 16,
            marginBottom: marginBottom || 0,
          }}
        />
      )}
    </div>
  );
};

export default CountOrdersChart;
